import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import MemberForm from "@/components/organization/Member/Form";
import { useUpdateMemberMutation } from "@/libs/generated/hooks";
import type { MembersQuery } from "@/libs/generated/operations";
import { queryClient } from "@/libs/queryClient";
import type { MemberFormSchemaType } from "@/schemas/organization-form/member.schema";
import { MemberFormSchemaDefaultValues } from "@/schemas/organization-form/member.schema";

type MemberRecord = MembersQuery["members"]["records"][0];

type EditMemberDialogProps = {
  open: boolean;
  onClose: () => void;
  member: MemberRecord | null;
  onSuccess?: () => void;
};

function EditMemberDialog({ open, onClose, member, onSuccess }: EditMemberDialogProps) {
  const { t } = useTranslation();
  const updateMember = useUpdateMemberMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["members"] });
    },
  });
  const loading = updateMember.isPending;
  const error = updateMember.error;
  const reset = updateMember.reset;
  const handleClose = () => {
    onClose();
    reset();
  };
  const handleSubmit = async (data: MemberFormSchemaType) => {
    if (!member?.id) {
      toast.error(t("network.errors.400"), { position: "bottom-right" });
      return;
    }

    try {
      const result = await updateMember.mutateAsync({
        UpdateMemberInput: {
          id: member.id,
          assigneeFirstName: data.assigneeFirstName,
          assigneeLastName: data.assigneeLastName,
          assigneeEmail: data.assigneeEmail,
          assigneePhone: data.assigneePhone,
          assigneeFunction: data.assigneeFunction,
          assigneeService: data.assigneeService,
          assigneeRank: Number(data.assigneeRank),
        },
      });

      if (result.updateMember) {
        toast.success(t("network.organization.member.updated"), { position: "bottom-right" });
        onSuccess?.();
        onClose();
      }
    } catch (_err) {
      // Error is handled by the mutation hook and displayed in the UI
    }
  };

  const getFormValues = (): MemberFormSchemaType => {
    if (!member) return MemberFormSchemaDefaultValues;

    return {
      id: member.id,
      assigneeFirstName: member.assigneeFirstName,
      assigneeLastName: member.assigneeLastName,
      assigneeEmail: member.assigneeEmail,
      assigneePhone: member.assigneePhone,
      assigneeFunction: member.assigneeFunction || "",
      assigneeService: member.assigneeService || "",
      assigneeRank: Number(member.assigneeRank),
    };
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{t("organization.actions.edit-member.title")}</DialogTitle>
      <DialogContent>
        {error != null && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error.message || t("network.errors.unknown")}
          </Alert>
        )}

        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" mb={2}>
            <CircularProgress size={20} />
            <span style={{ marginLeft: 8 }}>{t("common.loading")}</span>
          </Box>
        )}

        <MemberForm
          disabledFields={["assigneeEmail"]} // Email should not be editable
          unUsedFields={["id"]}
          values={getFormValues()}
          onSubmit={handleSubmit}
          title="user-info"
          label={t("organization.actions.edit-member.text")}
        />
      </DialogContent>
      <DialogActions>
        <Box display="flex" justifyContent="flex-end" gap={1} p={2}>
          {/* The form handles its own submission, so we don't need action buttons here */}
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default EditMemberDialog;
